import { useQuery } from '@abyss/web/hooks/useQuery';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants, errorCodeRouteMap } from './../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { convertObjectToUrlParams, getFeatureFlag } from '../../common/Utils';
import { PortalContext } from '../../context/PortalContext';
import { useLagoon } from '../../hooks/useLagoon';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../store/useAnalyticsStore/analyticsStore';
import { useErrorStore } from '../../store/useErrorStore';
import { ErrorStore } from '../../store/useErrorStore/errorStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { getBlueGreenEnv } from '../../utils/cookies.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { StoreKeys } from '../useStore/state';
import { useStore } from '../useStore/useStore';
import { useTranslationLog } from '../useTranslationLog/useTranslationLog';
import { getGraphqlUrl } from './helpers';

const getHeaders = (currentMember, portalData, typeaheadStore, isWidget) => {
  let headerOptions;

  // Get and set lob and population header
  let lob = typeaheadStore?.lob || currentMember?.lineOfBusiness;
  lob = lob ?? 'UNKNOWN';
  lob = lob.replace(/&/g, 'n').toLowerCase();
  const population = currentMember?.population?.toLowerCase() || lob;
  headerOptions = { lob, population };

  // Get and set portalName header
  if (portalData?.portalName) {
    headerOptions = { ...headerOptions, portal: portalData.portalName };
  }
  if (
    currentMember?.isIFPEarlyPay &&
    !headerOptions?.portal?.includes('ifp-earlypay')
  ) {
    headerOptions = { ...headerOptions, portal: 'ifp-earlypay' };
  }

  //Adding blue/green to query header based on override or LaunchDarkly Flag

  const host = window.location.hostname;
  const bgEnv = getBlueGreenEnv(host, isWidget);
  if (bgEnv) {
    headerOptions = { ...headerOptions, bgEnv };
  }

  return headerOptions;
};

export const useCustomQuery = (
  query,
  queryOptions
): [data: any, getData: (params: any) => any] => {
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const removeCookies: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_COOKIES
  );

  const allConfigs = useLagoon('config')();
  const cookieKeyRemoved = Array.isArray(allConfigs)
    ? allConfigs.find((row) => row.key === 'COOKIES_REMOVED')?.value
    : undefined;
  const { navigate } = useRouter();
  const { portalData } = useContext(PortalContext);
  const isWidget = useStore(StoreKeys.IS_WIDGET);

  const { linkName, originLinkNameForAnalytics } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
      originLinkNameForAnalytics:
        state.analyticsState.originLinkNameForAnalytics,
    }))
  );

  const { setErrorStore } = useErrorStore(
    useShallow((state: ErrorStore) => ({
      setErrorStore: state.setErrorStore,
    }))
  );

  const { typeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      typeaheadSearchStore: state.typeaheadSearchStore,
    }))
  );

  const currentMember = getCurrentMember();
  const headerOptions = getHeaders(
    currentMember,
    portalData,
    typeaheadSearchStore,
    isWidget
  );

  if (removeCookies) {
    const filteredCookies = cookieKeyRemoved
      ?.split(',')
      ?.map((prefix) => prefix?.trim());
    const currentDomain = window?.location?.hostname;
    const domainParts = currentDomain?.split('.');
    document?.cookie?.split(';')?.forEach((cookie) => {
      const [key] = cookie.split('=').map((item) => item.trim());
      if (filteredCookies?.some((prefix) => key?.startsWith(prefix))) {
        for (let i = 0; i < domainParts?.length - 1; i++) {
          const parentDomain = domainParts?.slice(i)?.join('.');
          document.cookie = `${key}=; expires=${new Date(
            0
          ).toUTCString()}; path=/; domain=.${parentDomain}`;
        }
      }
    });
  }

  const url = getGraphqlUrl(portalData, queryOptions);

  const translationLogger = useTranslationLog({}, headerOptions, url);

  const [queryResult, queryAction] = useQuery(query, {
    ...{ ...queryOptions, url },
    headers: { ...queryOptions.headers, ...headerOptions },
    onCompleted: (result) => {
      if (queryOptions.onCompleted) {
        queryOptions.onCompleted(result);
      }

      if (result?.data?.missingPSXCodeTranslations?.length) {
        translationLogger({
          variables: {
            missedTranslations: result.data.missingPSXCodeTranslations,
          },
        });
      }
    },
    onError: (error: any) => {
      // Casting error to any to safely access transport-layer properties that aren't on the base Error type.
      const gQLError = error?.response?.data?.errors?.[0]?.extensions;
      const status = gQLError?.status ? String(gQLError.status) : undefined;
      const statusRegex = /^(4|5)\d{2}$/;
      if (
        status &&
        statusRegex.test(status) &&
        gQLError?.pageType !== 'Custom' &&
        gQLError?.pageType !== 'Ignore' &&
        error?.operationName !== 'ProviderSearch'
      ) {
        const errorStoreValues = {
          message: gQLError?.message,
          code: gQLError?.status,
          source: error?.operationName,
          uiSection: linkName || originLinkNameForAnalytics,
        };
        setErrorStore(errorStoreValues);
        const urlParams = convertObjectToUrlParams(
          {} as any, // ChipState not required for error redirect; casting to any to satisfy signature.
          { ...errorStoreValues }
        );

        const guestDeeplinkFlow = sessionStorage.getItem(
          Constants.STORAGE_KEYS.SESSION.IS_GUEST_DEEPLINK_FLOW
        );

        if (guestDeeplinkFlow) {
          // Don't navigate if already on error page to prevent redirect loops
          if (!window.location.pathname.includes('/unable-to-load-plan')) {
            navigate('/unable-to-load-plan');
          }
        } else {
          navigate(
            `${
              errorCodeRouteMap[gQLError?.status] || errorCodeRouteMap['4xx']
            }${urlParams}`
          );
        }
      }
      if (queryOptions.onError && typeof queryOptions.onError === 'function') {
        queryOptions.onError(gQLError);
      }
    },
  });
  return [queryResult, queryAction];
};
